import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { updateCalendarDataSelfDate, incrementPredictionUpdateCounter } from '../Redux/calendarDataSelfSlice';
import { incrementOperation, decrementOperation, setOperationError } from '../Redux/loadingSlice';
import { saveCalendarDataToCache, loadCalendarDataFromCache } from '../data/calendarCache';
import { computeNewDayData } from '../data/updateCalendarDayField';
import { FIELD_TO_COLUMN } from '../data/calendarConstants';

const TAG = '[useUpdateCalendarDay]';

/**
 * Hook to update a single boolean field on a calendar day (e.g. periodDay, inseminationDay).
 *
 * Optimistic update flow:
 *   1. Compute new day data and update Redux immediately (zero delay)
 *   2. Show global "Saving..." indicator via loading slice counter
 *   3. Persist to Supabase (UPDATE existing row, or INSERT if none exists)
 *   4. Update localStorage cache so the change survives a reload
 *   5. If Supabase fails, revert Redux back to old data and show error
 *   6. Always decrement loading counter in finally
 *
 * @returns {{ updateCalendarDay: (date: string, field: string, value: boolean) => Promise<boolean> }}
 */
export default function useUpdateCalendarDay() {
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user.profile?.id);
  const reduxCalendar = useSelector((s) => s.calendarDataSelf.data);

  const updateCalendarDay = async (date, field, value) => {
    if (!currentUserId) {
      return false;
    }

    const column = FIELD_TO_COLUMN[field];
    if (!column) {
      return false;
    }


    // ── 1. Compute new day data and optimistically update Redux ──────────
    const currentDayData = reduxCalendar?.[date] || {};
    const newDayData = computeNewDayData(currentDayData, field, value);


    dispatch(updateCalendarDataSelfDate({ date, data: newDayData }));

    // Increment prediction update counter if period day changed (affects cycle calculations)
    if (field === 'periodDay') {
      dispatch(incrementPredictionUpdateCounter());
    }

    // ── 2. Show global "Saving..." indicator ────────────────────────────
    dispatch(incrementOperation('calendar'));

    try {
      // ── 3. Persist to Supabase (single upsert call) ───────────────────
      // Uses the UNIQUE(user_id, day) constraint on calendar_days.
      // If a row for this user+date exists, it UPDATEs; otherwise it INSERTs.
      const upsertPayload = { user_id: currentUserId, day: date, [column]: value, updated_at: new Date().toISOString() };
      const { error: upsertError } = await supabase
        .from('calendar_days')
        .upsert(upsertPayload, { onConflict: 'user_id,day' });

      if (upsertError) {
        dispatch(updateCalendarDataSelfDate({ date, data: currentDayData }));
        const msg = upsertError.message?.toLowerCase();
        dispatch(setOperationError({ type: 'calendar', error: msg?.includes('network') || msg?.includes('fetch') ? 'Error: No internet connection' : 'Error: Failed to save' }));
        return false;
      }


      // ── 4. Update localStorage cache ────────────────────────────────────
      const cached = loadCalendarDataFromCache();
      if (cached?.data) {
        cached.data[date] = newDayData;
        saveCalendarDataToCache(cached);
      } else {
      }

      return true;
    } catch (err) {
      dispatch(updateCalendarDataSelfDate({ date, data: currentDayData }));
      const msg = err.message?.toLowerCase();
      dispatch(setOperationError({ type: 'calendar', error: msg?.includes('network') || msg?.includes('fetch') ? 'Error: No internet connection' : 'Error: Failed to save' }));
      return false;
    } finally {
      // ── 5. Always decrement loading counter ────────────────────────────
      dispatch(decrementOperation('calendar'));
    }
  };

  return { updateCalendarDay };
}
